import {
  discountCoversService,
  DiscountCoverage,
  getEligibleServiceIds,
  isDiscountCodeRedeemable,
  validateDiscountCode,
} from '@/util/functions/discountCodeValidator';
import { DiscountCode } from '@/util/types/serverTypeCollection/discounts';

/**
 * Query-param names a scheduling link may carry to pre-fill the scheduler.
 *
 * Declared once so the writer (`SchedulerURLGenerator` in scheduling settings)
 * and the reader (`/scheduler/page.tsx`) can never drift apart.
 */
export const SCHEDULER_PREFILL_PARAMS = {
  primaryService: 'service',
  bundle: 'bundle',
  addons: 'addons',
  role: 'role',
  promo: 'promo',
} as const;

export type SchedulerPrefillNoticeCode =
  /** The linked primary service no longer exists or is hidden from the scheduler. */
  | 'service-unavailable'
  /** The linked service can only be booked as an add-on, so it was demoted. */
  | 'service-not-primary'
  /** The linked bundle no longer exists or is inactive. */
  | 'bundle-unavailable'
  /** One or more linked add-ons no longer exist or are hidden from the scheduler. */
  | 'addon-unavailable'
  /** The linked role no longer exists for this company. */
  | 'role-unavailable'
  /** The linked role is not one the linked primary service is offered to. */
  | 'role-service-mismatch'
  /** The linked discount code is unknown, inactive, expired, or not applicable. */
  | 'promo-unavailable'
  /** An applied discount stopped applying because the cart changed. */
  | 'promo-removed';

export type SchedulerPrefillNotice = {
  code: SchedulerPrefillNoticeCode;
  /** Customer-facing copy. Never leaks why a service is hidden internally. */
  message: string;
};

export type SchedulerPrefill = {
  /** True when the link carried at least one pre-fill param (valid or not). */
  requested: boolean;
  primaryServiceId: string | null;
  bundleId: string | null;
  addonServiceIds: string[];
  roleId: string | null;
  discountCode: DiscountCode | null;
  /**
   * Service ids the discount is pinned to. Empty means "every service in the
   * cart" — the pricing engine treats an empty `_serviceIds` on an
   * `appliesToAllServices` code as covering everything, so the discount keeps
   * following the cart while the customer is still assembling it.
   */
  discountServiceIds: string[];
  /**
   * A live code the link carried that nothing in the cart qualifies for *yet*.
   *
   * Held rather than discarded: a campaign that advertises "book an inspection,
   * get half off radon" lands with the code matching nothing, and the customer
   * is meant to add the qualifying service during the flow. Dropping the code on
   * arrival meant doing exactly that earned them nothing, because by then there
   * was nothing left to apply. `SchedulerContext` promotes this to
   * `appliedDiscountCode` the moment a covered service enters the cart.
   *
   * Only ever set for a code that is active, in date, and covers at least one
   * service this scheduler can actually offer — a code that could never apply is
   * refused with a notice instead, exactly as before.
   *
   * Mutually exclusive with `discountCode`.
   */
  pendingDiscountCode: DiscountCode | null;
  notices: SchedulerPrefillNotice[];
};

export const EMPTY_SCHEDULER_PREFILL: SchedulerPrefill = {
  requested: false,
  primaryServiceId: null,
  bundleId: null,
  addonServiceIds: [],
  roleId: null,
  discountCode: null,
  discountServiceIds: [],
  pendingDiscountCode: null,
  notices: [],
};

/** Minimal service shape the resolver reads. `ServWithMods` satisfies it. */
export type PrefillCatalogService = {
  _id: string;
  name?: string;
  excludeFromScheduler?: boolean;
  /** When false the service cannot be booked as the primary service. */
  canBePrimary?: boolean;
  /** Roles the service is offered to. Empty/absent means "any role". */
  _contactRoleId?: string[];
};

/** Minimal bundle shape the resolver reads. `Bundle` satisfies it. */
export type PrefillCatalogBundle = {
  _id: string;
  active?: boolean;
  items?: { _serviceId?: string }[];
};

/** Minimal contact-role shape the resolver reads. `ContactRoleType` satisfies it. */
export type PrefillCatalogRole = {
  _id: string;
  name?: string;
};

export type RawSearchParams = Record<string, string | string[] | undefined>;

export type ResolveSchedulerPrefillInput = {
  searchParams: RawSearchParams;
  services: PrefillCatalogService[];
  bundles: PrefillCatalogBundle[];
  contactRoles: PrefillCatalogRole[];
  discountCodes: DiscountCode[];
};

/** First non-empty value of a Next.js search param, trimmed. */
export function firstSearchParamValue(
  value: string | string[] | undefined
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Parses an id list param. Accepts both `addons=a,b` and repeated
 * `addons=a&addons=b`, since hand-built marketing links use either.
 */
function parseIdListParam(value: string | string[] | undefined): string[] {
  const entries = Array.isArray(value) ? value : value ? [value] : [];
  return entries
    .flatMap((entry) => entry.split(','))
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function serviceLabel(service: PrefillCatalogService): string {
  return service.name?.trim() || 'That service';
}

/**
 * Services the customer could add to make a code that does not apply yet start
 * applying: everything this scheduler offers that the code covers, minus what
 * the cart already holds.
 *
 * Generic over the service shape so the resolver (catalog services) and
 * `SchedulerContext` (`ServWithMods`) can both call it without converting.
 *
 * An `appliesToAllServices` code returns nothing: it covers the whole catalog,
 * so there is no service to single out — and such a code applies the moment the
 * cart is non-empty, so it is never the one waiting to be unlocked.
 */
export function getDiscountUnlockingServices<T extends PrefillCatalogService>(
  discountCode: DiscountCoverage,
  services: T[],
  cartServiceIds: string[]
): T[] {
  if (discountCode.appliesToAllServices) return [];

  const inCart = new Set(cartServiceIds);
  return services.filter(
    (service) =>
      Boolean(service?._id) &&
      service.excludeFromScheduler !== true &&
      !inCart.has(service._id) &&
      discountCoversService(discountCode, service._id)
  );
}

/**
 * What to tell a booker holding a code that does not apply to their cart yet.
 *
 * Names the service when exactly one would unlock the code, and otherwise says
 * how many would — so the message is always a next step rather than a refusal.
 * Owned here, in one place, because the top-of-funnel banner and the tests both
 * read it.
 *
 * Callers must pass a non-empty list; a code nothing can unlock is refused
 * outright rather than left pending.
 */
export function discountPendingMessage(
  code: string,
  unlockingServices: PrefillCatalogService[]
): string {
  if (unlockingServices.length === 1) {
    return `Discount code ${code}: Add ${serviceLabel(
      unlockingServices[0]
    )} To Apply`;
  }

  return `Discount code ${code} applies to (${unlockingServices.length}) services. Select one to apply`;
}

/**
 * Turns the pre-fill params on a scheduling link into a sanitized cart.
 *
 * Every id is validated against the company's own catalog, so a stale or
 * hand-edited link degrades into a normal working scheduler plus a customer-
 * facing notice — it never throws and never blocks the funnel. Anything the
 * scheduler itself would not offer (services flagged `excludeFromScheduler`,
 * inactive bundles, roles from another company) is refused, so a link can't
 * reach past the UI's own visibility rules.
 *
 * Pure and dependency-free so it can run in the server component that already
 * fetches the catalogs, before the first byte of HTML is sent. That avoids a
 * flash of empty cart and keeps it out of `DraftLoader`'s way.
 */
export function resolveSchedulerPrefill({
  searchParams,
  services,
  bundles,
  contactRoles,
  discountCodes,
}: ResolveSchedulerPrefillInput): SchedulerPrefill {
  const requestedPrimaryServiceId = firstSearchParamValue(
    searchParams[SCHEDULER_PREFILL_PARAMS.primaryService]
  );
  const requestedBundleId = firstSearchParamValue(
    searchParams[SCHEDULER_PREFILL_PARAMS.bundle]
  );
  const requestedAddonIds = parseIdListParam(
    searchParams[SCHEDULER_PREFILL_PARAMS.addons]
  );
  const requestedRoleId = firstSearchParamValue(
    searchParams[SCHEDULER_PREFILL_PARAMS.role]
  );
  const requestedPromo = firstSearchParamValue(
    searchParams[SCHEDULER_PREFILL_PARAMS.promo]
  );

  const requested = Boolean(
    requestedPrimaryServiceId ||
      requestedBundleId ||
      requestedAddonIds.length > 0 ||
      requestedRoleId ||
      requestedPromo
  );

  if (!requested) return EMPTY_SCHEDULER_PREFILL;

  const notices: SchedulerPrefillNotice[] = [];

  // Only services the scheduler itself would offer are addressable by a link.
  const schedulableServices = new Map<string, PrefillCatalogService>();
  services.forEach((service) => {
    if (service?._id && service.excludeFromScheduler !== true) {
      schedulableServices.set(service._id, service);
    }
  });

  // --- Primary service -----------------------------------------------------
  let primaryServiceId: string | null = null;
  const demotedAddonIds: string[] = [];

  if (requestedPrimaryServiceId) {
    const service = schedulableServices.get(requestedPrimaryServiceId);

    if (!service) {
      notices.push({
        code: 'service-unavailable',
        message:
          'The service from your link is no longer available online. Please choose from the options below.',
      });
    } else if (service.canBePrimary === false) {
      // Add-on-only service (e.g. a radon test promoted in a campaign). Keep
      // the customer's intent by moving it to the add-ons instead of dropping
      // it, then let them pick the inspection it attaches to.
      demotedAddonIds.push(service._id);
      notices.push({
        code: 'service-not-primary',
        message: `${serviceLabel(
          service
        )} can only be booked alongside an inspection, so we kept it as an add-on. Please choose your main inspection below.`,
      });
    } else {
      primaryServiceId = service._id;
    }
  }

  // --- Bundle --------------------------------------------------------------
  const bundle = requestedBundleId
    ? bundles.find((b) => b?._id === requestedBundleId && b.active !== false)
    : undefined;

  if (requestedBundleId && !bundle) {
    notices.push({
      code: 'bundle-unavailable',
      message:
        'The package from your link is no longer available, so we cleared it from your cart.',
    });
  }

  // --- Add-ons -------------------------------------------------------------
  const addonServiceIds: string[] = [];
  let unavailableAddonCount = 0;

  const addAddon = (serviceId: string) => {
    if (serviceId === primaryServiceId) return; // already in the cart
    if (addonServiceIds.includes(serviceId)) return;
    addonServiceIds.push(serviceId);
  };

  requestedAddonIds.forEach((serviceId) => {
    if (schedulableServices.has(serviceId)) addAddon(serviceId);
    else unavailableAddonCount += 1;
  });
  // Already validated above, so these skip the catalog check.
  demotedAddonIds.forEach(addAddon);

  if (unavailableAddonCount > 0) {
    notices.push({
      code: 'addon-unavailable',
      message:
        unavailableAddonCount === 1
          ? 'One add-on from your link is no longer available and was not added.'
          : `${unavailableAddonCount} add-ons from your link are no longer available and were not added.`,
    });
  }

  // Mirrors how `SchedulerContext` derives `selectedServiceIds`, so discount
  // eligibility here matches what the cart will report a moment later.
  const cartServiceIds = [
    ...new Set([
      ...(primaryServiceId ? [primaryServiceId] : []),
      ...addonServiceIds,
      ...(bundle?.items ?? [])
        .map((item) => item?._serviceId)
        .filter((id): id is string => Boolean(id)),
    ]),
  ];

  // --- Role ----------------------------------------------------------------
  let roleId: string | null = null;

  if (requestedRoleId) {
    const role = contactRoles.find((r) => r?._id === requestedRoleId);
    const primaryService = primaryServiceId
      ? schedulableServices.get(primaryServiceId)
      : undefined;
    const serviceRoleIds = primaryService?._contactRoleId ?? [];

    if (!role) {
      notices.push({
        code: 'role-unavailable',
        message:
          'We could not apply the role from your link. Please confirm who you are below.',
      });
    } else if (serviceRoleIds.length > 0 && !serviceRoleIds.includes(role._id)) {
      // The service is not offered to that role — let `ContactForm` re-derive
      // the valid roles from the service rather than book a mismatch.
      notices.push({
        code: 'role-service-mismatch',
        message:
          'We could not apply the role from your link to this service. Please confirm who you are below.',
      });
    } else {
      roleId = role._id;
    }
  }

  // --- Discount code -------------------------------------------------------
  let discountCode: DiscountCode | null = null;
  let discountServiceIds: string[] = [];
  let pendingDiscountCode: DiscountCode | null = null;

  if (requestedPromo) {
    const normalizedPromo = requestedPromo.toUpperCase();
    const validation = validateDiscountCode(
      normalizedPromo,
      discountCodes,
      cartServiceIds
    );

    if (validation.isValid && validation.discountCode) {
      discountCode = validation.discountCode;
      // Leave the pin list empty for an all-services code so the discount
      // covers whatever ends up in the cart — a link-applied code is set
      // before the cart is finished.
      discountServiceIds = validation.discountCode.appliesToAllServices
        ? []
        : getEligibleServiceIds(validation.discountCode, cartServiceIds);
    } else {
      // The code did not apply. Separate "wrong for this cart, for now" from
      // "could never work": the first is a campaign working as designed — the
      // qualifying service is meant to be added during the flow — so the code
      // is carried instead of dropped. The second is still refused outright,
      // with the same wording as before.
      const requestedCode = discountCodes.find(
        (candidate) => candidate.code.toUpperCase() === normalizedPromo
      );
      const unlockingServices =
        requestedCode && isDiscountCodeRedeemable(requestedCode)
          ? getDiscountUnlockingServices(requestedCode, services, cartServiceIds)
          : [];

      if (requestedCode && unlockingServices.length > 0) {
        pendingDiscountCode = requestedCode;
      } else {
        notices.push({
          code: 'promo-unavailable',
          message: `We could not apply the discount code ${normalizedPromo}.${
            validation.error ? ` ${validation.error}` : ''
          }`,
        });
      }
    }
  }

  return {
    requested,
    primaryServiceId,
    bundleId: bundle?._id ?? null,
    addonServiceIds,
    roleId,
    discountCode,
    discountServiceIds,
    pendingDiscountCode,
    notices,
  };
}

/**
 * True when the link named a primary service the scheduler could not honor —
 * add-on-only, or since removed — leaving the cart without one.
 *
 * Those links never run the service questionnaire, so the "Select A Primary
 * Service" step has nothing to list. The services step opens in search mode
 * instead, which offers the whole bookable catalog and so actually delivers what
 * the customer is told to do ("choose your main inspection below").
 */
export function prefillNeedsPrimaryServiceSearch(
  prefill: SchedulerPrefill
): boolean {
  return (
    prefill.requested &&
    !prefill.primaryServiceId &&
    prefill.notices.some(
      (notice) =>
        notice.code === 'service-not-primary' ||
        notice.code === 'service-unavailable'
    )
  );
}

/** Notice shown when a cart change invalidates an already-applied discount. */
export function discountRemovedNotice(code: string): SchedulerPrefillNotice {
  return {
    code: 'promo-removed',
    message: `Discount code ${code} no longer applies to your selected services, so it was removed.`,
  };
}
